import {
  MailingBaseFormDto,
  falseWhenTypeNotIn,
  trueWhenTypeNotIn,
  type MailingSerdeContextDataInterface,
  type MailingBaseFormInterface,
} from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-base-form.dto';
import { MailingTypeEnum } from '@zaoblako/shared/modules/notification/enums/mailing.type.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { NotificationDtoKeysEnum } from '@zaoblako/shared/modules/notification/enums/notification-dto-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';
import type { BillTargetItemInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/bill-target-item.dto';
import type { DocumentTargetItemInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/document-target-item.dto';

import * as yup from 'yup';

/** Mailing create and update APIs request body - DTO signature */
export interface MailingFormInterface extends MailingBaseFormInterface {
  /** Mailing launch date */
  date: Date;
  /**
   * Bills or documents search period start date. Using only when {@link MailingFormInterface.type}
   * property equals {@link MailingTypeEnum.DEBTORS}, {@link MailingTypeEnum.BILLS} or {@link MailingTypeEnum.DOCUMENTS}.
   */
  attachmentStartDate?: Date;
  /**
   * Bills or documents search period end date. Using only when {@link MailingFormInterface.type}
   * property equals {@link MailingTypeEnum.DEBTORS},{@link MailingTypeEnum.BILLS} or {@link MailingTypeEnum.DOCUMENTS}.
   */
  attachmentEndDate?: Date;
  /**
   * Bills to attach. Using only when {@link MailingFormInterface.type}
   * property equals {@link MailingTypeEnum.BILLS}.
   */
  bills?: BillTargetItemInterface[];
  /**
   * Documents to attach. Using only when {@link MailingFormInterface.type}
   * property equals {@link MailingTypeEnum.DOCUMENTS}.
   */
  documents?: DocumentTargetItemInterface[];
}

const attachementDateOptional = (
  context: SerdeContextInterface<MailingSerdeContextDataInterface, MailingBaseFormInterface>,
): boolean => {
  const mailingType = context?.data?.type || context?.value?.type;

  const isBillsOrDocuments = [MailingTypeEnum.BILLS, MailingTypeEnum.DOCUMENTS].includes(mailingType);
  const isDebtorsWithBillsAttach = mailingType === MailingTypeEnum.DEBTORS && context?.value?.attachBills;

  return !isBillsOrDocuments && !isDebtorsWithBillsAttach;
};

/** Mailing create and update APIs request body - DTO schema */
export const MailingFormDto = SerdeDtoClass.from<MailingBaseFormInterface, MailingFormInterface>(
  MailingBaseFormDto,
  NotificationDtoKeysEnum.MAILING_FORM_DTO,
  {
    date: {
      label: 'Дата отправки',
      serializer: yup.date(),
    },
    attachmentStartDate: {
      optional: attachementDateOptional,
      hidden: trueWhenTypeNotIn(MailingTypeEnum.DEBTORS, MailingTypeEnum.BILLS, MailingTypeEnum.DOCUMENTS),
      label: 'Документы и счета - начало периода',
      serializer: yup.date(),
    },
    attachmentEndDate: {
      optional: attachementDateOptional,
      hidden: trueWhenTypeNotIn(MailingTypeEnum.DEBTORS, MailingTypeEnum.BILLS, MailingTypeEnum.DOCUMENTS),
      label: 'Документы и счета - конец периода',
      serializer: yup.date(),
    },
    bills: {
      optional: falseWhenTypeNotIn(MailingTypeEnum.BILLS),
      hidden: trueWhenTypeNotIn(MailingTypeEnum.BILLS),
      label: 'Счета для отправки',
      serializer: (
        context: SerdeContextInterface<MailingSerdeContextDataInterface, MailingFormInterface>,
      ) => (context?.data?.type || context?.value?.type) === MailingTypeEnum.BILLS
        ? yup
          .array(yup.object({ erp1cRef: nonEmptyStringSchema }) as yup.Schema<any>)
          .min(1, DtoMessageEnum.EMPTY_ARRAY)
        : yup.array(yup.object({ erp1cRef: yup.string() })),
    },
    documents: {
      optional: falseWhenTypeNotIn(MailingTypeEnum.DOCUMENTS),
      hidden: trueWhenTypeNotIn(MailingTypeEnum.DOCUMENTS),
      label: 'Счета для отправки',
      serializer: (
        context: SerdeContextInterface<MailingSerdeContextDataInterface, MailingFormInterface>,
      ) => (context?.data?.type || context?.value?.type) === MailingTypeEnum.DOCUMENTS
        ? yup
          .array(yup.object({ erp1cRef: nonEmptyStringSchema }) as yup.Schema<any>)
          .min(1, DtoMessageEnum.EMPTY_ARRAY)
        : yup.array(yup.mixed()),
    },
  },
);
